import {BaseItem, SuggestionProviderItemsWithOptions, WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import {useSpeechRecognition} from "@vueuse/core";
import {Ref, shallowRef, watch} from "vue";
import {QInput} from "quasar";
import ClientCache from "@/utils/login/ClientCache.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import type {ICellEditorParams, ICellRendererParams} from "ag-grid-community";
import SearchingAndSuggestionUtil from "@/utils/editView/widgets/SearchingAndSuggestionUtil.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import {FromGridAction} from "@/utils/UIConstants/enums.ts";
import FieldMetaData = namaTypes.FieldMetaData;
import GenericValue = namaTypes.GenericValue;

interface LanguageInfo extends BaseItem {
  timeZoneId?: string
}

export default class BigTextFieldWidgetUtil extends FieldWidgetUtil {
  static AudioSpeechContextLanguageKey = "audioSpeechContextLanguage";
  public static readonly speechLanguages: LanguageInfo[] = [
    {
      label: "ar-Egypt",
      value: "AR_EG",
      timeZoneId: "Africa/Cairo"
    },
    {
      label: "en-USA",
      value: "EN_US",
      timeZoneId: "America/New_York"
    },
    {
      label: "ar-Kuwait",
      value: "AR_KW",
      timeZoneId: "Asia/Kuwait"
    },
    {
      label: "fr-Algeria",
      value: "FR_FR",
      timeZoneId: "Africa/Algiers",
    },
    {
      label: "ar-Saudi_Arabia",
      value: "AR_SA",
      timeZoneId: "Asia/Riyadh",
    },
  ];

  public lang = shallowRef();
  public speech = useSpeechRecognition({
    lang: this.lang
  });
  private static needsMicrophoneAccess = true;

  constructor(presenter: WidgetsPresenter, fieldMetaData: FieldMetaData, agCellParams?: ICellRendererParams | ICellEditorParams) {
    super(presenter, fieldMetaData)
    watch(this.speech.isFinal, async (isFinalValue) => {
      if (!isFinalValue)
        return;
      this.speech.stop();
      const text = this.fetchFieldValue() + " " + this.speech.result.value;
      this.onPartialUserInput(text)
      await this.onUserInputFinished();
    });
    this.setInitialCountryLanguage();
    if (agCellParams) {
      this.agCellParams = agCellParams;
      if (agCellParams.value)
        this.inputFieldValueRef.value = agCellParams.value
    }
  }

  public async startSpeechRecognition() {
    if (this.inputFieldValueRef.value != this.fetchFieldValue())
      await this.onUserInputFinished();
    if (!this.speech.isSupported.value)
      return;
    if (this.speech.isListening.value)
      return this.speech.stop();
    if (BigTextFieldWidgetUtil.needsMicrophoneAccess)
      await this.askForMicrophoneAccess()
    this.speech.start();
  }

  onSpeechLanguageChange(lang: string) {
    this.lang.value = lang;
    localStorage.setItem(BigTextFieldWidgetUtil.AudioSpeechContextLanguageKey, lang);
  }

  private async askForMicrophoneAccess() {
    if (location.protocol == "http:" && location.hostname != "localhost" && !location.hostname.startsWith("127.0.0."))
      useErrorsStore().pushErrorStr("Microphone access is blocked over HTTP. Please use HTTPS or localhost.");
    try {
      await navigator.permissions.query({name: "microphone"}).then((perm) => {
        if (perm.state == "granted")
          BigTextFieldWidgetUtil.needsMicrophoneAccess = false;
        else
          return navigator.mediaDevices.getUserMedia({audio: true})
          .then(stream => {
            if (!stream)
              return;
            const wn = <any>window;
            wn.localStream = stream;
            BigTextFieldWidgetUtil.needsMicrophoneAccess = false
          })
      })
    } catch (e) {
      console.log(e);
      useErrorsStore().pushErrorStr("Microphone access failed")
    }
  }

  public insertNewLine(qElementRef: Ref<QInput | null>) {
    const nativeElement = qElementRef.value?.getNativeElement()
    if (!nativeElement)
      return;
    const cursorPos = nativeElement.selectionStart;
    if (!cursorPos)
      return;
    const textBefore = nativeElement.value.substring(0, cursorPos);
    const textAfter = nativeElement.value.substring(cursorPos);
    const value = textBefore + '\n' + textAfter;
    this.onPartialUserInput(value);
  }

  public static getDefaultCountryLanguage() {
    const currentTimeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone
    for (const speechLanguage of BigTextFieldWidgetUtil.speechLanguages) {
      if (speechLanguage.timeZoneId == currentTimeZoneId)
        return speechLanguage.value;
    }
    return 'AR_EG';
  }

  private setInitialCountryLanguage() {
    //levels (localStorage=>backend=>defaultLanguage)
    const localValue = localStorage.getItem(BigTextFieldWidgetUtil.AudioSpeechContextLanguageKey);
    if (localValue) {
      this.lang.value = localValue;
      return;
    }
    let audioSpeechContextLanguage: string | undefined;
    audioSpeechContextLanguage = ClientCache.getCustomGUISettings()?.audioSpeechContextLanguage;
    if (!audioSpeechContextLanguage)
      audioSpeechContextLanguage = BigTextFieldWidgetUtil.getDefaultCountryLanguage()
    this.lang.value = audioSpeechContextLanguage;
  }

  async suggest(val: string | null | undefined, options: Ref<namaTypes.SuggestionProvidedItem[] | undefined>) {
    let contextFields: Record<string, GenericValue> = {};
    const rowIndex = (this.agCellParams as ICellEditorParams)?.rowIndex ?? -1
    this.presenter.fetchDispatcher().addContextFieldsToMap(this.fieldId(), this.createContextFieldsIds(), rowIndex
      , contextFields, FromGridAction.Yes, [], {});
    let placeExtraFilter;
    if (this.presenter.isEditPresenter())
      placeExtraFilter = (this.presenter as EditPresenter).getPlaceExtraFieldFilterId();
    const suggestions = await SearchingAndSuggestionUtil.suggest({
      fieldId: this.fieldId(),
      entityType: this.presenter.entityType,
      contextFields,
      currentText: val,
      placeExtraFilter,
      rowIndex
    }) ?? [];
    options.value = suggestions;
  }

  getSuggestionText(val:SuggestionProviderItemsWithOptions){
    return ObjectChecker.getFirstNotEmptyObj(val.displayCode, val.optionDisplayCode, val.optionText, val.optionCaption)
  }
}
